import { useState } from "react";
import Popup from "reactjs-popup";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import {
  useGenerateApiKey,
  useGetApiKeys,
} from "../../lib/queries/queriesAndMutations";
import ApiKeyCard from "./ApiKeyCard";
import Loader from "../../shared/Loader";
import icons from "../../lib/icons";
import images from "../../lib/images";

const ApiKeysPage = () => {
  const queryClient = useQueryClient();
  const { data: apiKeys, isLoading, isError } = useGetApiKeys();
  const { mutateAsync: createApiKey, isPending } = useGenerateApiKey();

  const [title, setTitle] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleGenerateApiKey = async (close) => {
    try {
      const res = await createApiKey(title);
      if ([200, 201].includes(res.status)) {
        toast.success("عملیات موفق");
        queryClient.invalidateQueries({ queryKey: ["getApiKeys"] });
        close();
      }
    } catch (err) {
      console.error("Error generating API key:", err);
    }
  };

  const handleSubmit = (close) => {
    if (!title.trim()) {
      toast.error("عنوان الزامی است");
      return;
    }

    if (!acceptTerms) {
      toast.error("ایجاد کلید دسترسی جدید مستلزم پذیرش قوانین است");
      return;
    }

    handleGenerateApiKey(close);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-4">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-red-500 text-center py-4">
        خطا: دریافت کلیدهای API با مشکل مواجه شد
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {apiKeys?.count > 0 ? (
        <div className="flex gap-1 overflow-x-scroll vanda">
          {apiKeys.results.map((item) => (
            <ApiKeyCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <NoKeysPlaceholder />
      )}

      <ApiKeyPopup
        isPending={isPending}
        title={title}
        setTitle={setTitle}
        acceptTerms={acceptTerms}
        setAcceptTerms={setAcceptTerms}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default ApiKeysPage;

/**
 * Popup Component
 */
const ApiKeyPopup = ({
  isPending,
  title,
  setTitle,
  acceptTerms,
  setAcceptTerms,
  onSubmit,
}) => {
  return (
    <Popup
      trigger={
        <button className="btn-edit flex items-center gap-1 mr-auto">
          <p>کلید دسترسی جدید</p>
          <img
            src={icons.plus}
            alt="edit-icon"
            className="invert"
            width={16}
            height={16}
          />
        </button>
      }
      modal
      nested
      closeOnDocumentClick={!isPending}
    >
      {(close) => (
        <div
          className="w-full bg-white mb-5 rounded-v-lg p-4 flex flex-col gap-4 max-w-[80vw] max-h-[90vh] mx-auto overflow-y-auto"
          dir="rtl"
        >
          <img src={images.api_key} alt="" className="w-full" />
          <TermsSection />

          <label className="flex items-center gap-4 text-md font-medium text-gray-700">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="w-4 h-4 border-gray-300 rounded focus:ring-primary-200"
              disabled={isPending}
            />
            شرایط بالا را می‌پذیرم
          </label>
          <div className="space-y-1">
            <label
              htmlFor="api-key-title"
              className="text-sm font-medium text-gray-700"
            >
              عنوان کلید دسترسی{" "}
            </label>
            <input
              id="api-key-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="p-2 bg-neutral-10 border-gray-400 rounded focus:ring-primary-200 focus:border-primary-200 w-full placeholder:text-[#999999] placeholder:text-[12px]"
              placeholder="عنوانی برای API Key وارد کنید"
              disabled={isPending}
            />
          </div>

          <div className="flex mr-auto gap-2">
            <button
              onClick={close}
              className="secondary-btn"
              disabled={isPending}
            >
              انصراف
            </button>
            <button
              onClick={() => onSubmit(close)}
              className="primary-btn"
              disabled={isPending}
            >
              {isPending ? (
                <div className="w-8 h-8 mx-auto">
                  <Loader />
                </div>
              ) : (
                "ساخت API Key"
              )}
            </button>
          </div>
        </div>
      )}
    </Popup>
  );
};

/**
 * Terms Text Section
 */
const TermsSection = () => (
  <>
    <p className="font-semibold text-justify text-sm text-gray-800">
      برای استفاده از API Key در کیف پول مجازی، کاربر موظف است تمامی قوانین و
      مقررات تعیین‌شده را رعایت کند.
    </p>
    <p className="text-justify text-sm text-gray-800">
      هرگونه سوءاستفاده یا نقض قوانین منجر به مسدودسازی دسترسی خواهد شد. با
      ایجاد کلید دسترسی، کاربر متعهد می‌شود مسئولیت کامل استفاده از آن را بر
      عهده بگیرد.
    </p>
  </>
);

/**
 * Placeholder for no keys
 */
const NoKeysPlaceholder = () => (
  <div className="flex flex-col items-center p-6 rounded-md">
    <img
      src="/icons/key-alt.svg"
      alt="کلید دسترسی"
      className="max-w-[40px] max-h-[40px]"
    />
    <p>کلید دسترسی برای شما موجود نمی باشد!</p>
  </div>
);
